import { Chip, Grid, TextField, alpha } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import * as React from "react";
import { Control, Controller } from "react-hook-form";
import {
	RECIPIENT_TYPE_COLOR,
	type RecipientType,
} from "../../../emailTopics/models/EmailAddressTopic";
import { EmailTopic } from "../../../emailTopics/models/EmailTopic";

type TopicFieldName = "TopicIdsTO" | "TopicIdsCC" | "TopicIdsBCC";

const RECIPIENT_FIELDS: { name: TopicFieldName; type: RecipientType }[] = [
	{ name: "TopicIdsTO", type: "TO" },
	{ name: "TopicIdsCC", type: "CC" },
	{ name: "TopicIdsBCC", type: "BCC" },
];

// The form holds topic ids while Autocomplete works with the topic objects, so each side is
// translated on the way in and on the way out.
const TopicSelector = ({
	control,
	name,
	type,
	topics,
}: {
	control: Control<any>;
	name: TopicFieldName;
	type: RecipientType;
	topics: EmailTopic[];
}) => {
	const color = RECIPIENT_TYPE_COLOR[type];

	return (
		<Controller
			name={name}
			control={control}
			render={({ field }) => (
				<Autocomplete
					multiple
					size="small"
					options={topics}
					value={topics.filter((topic) =>
						(field.value ?? []).includes(topic.EmailTopicId),
					)}
					getOptionLabel={(topic: EmailTopic) => topic.EmailTopicName}
					getOptionSelected={(option, value) =>
						option.EmailTopicId === value.EmailTopicId
					}
					onChange={(_event, selected) =>
						field.onChange(
							(selected as EmailTopic[]).map((topic) => topic.EmailTopicId),
						)
					}
					renderTags={(value, getTagProps) =>
						(value as EmailTopic[]).map((topic, index) => (
							<Chip
								{...getTagProps({ index })}
								key={topic.EmailTopicId}
								label={topic.EmailTopicName}
								size="small"
								variant="outlined"
								style={{
									color,
									borderColor: alpha(color, 0.5),
									backgroundColor: alpha(color, 0.08),
								}}
							/>
						))
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label={type}
							variant="outlined"
							margin="dense"
							placeholder="Topics"
						/>
					)}
				/>
			)}
		/>
	);
};

export const EmailAddressFormFields = ({
	control,
	topics,
}: {
	control: Control<any>;
	topics: EmailTopic[];
}) => (
	<Grid container spacing={2}>
		<Grid item xs={12}>
			<Controller
				name="Address"
				control={control}
				rules={{
					required: "Address is required",
					pattern: {
						value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
						message: "Enter a valid email address",
					},
				}}
				render={({ field, fieldState: { error } }) => (
					<TextField
						{...field}
						label="Address"
						variant="outlined"
						error={!!error}
						helperText={error ? error.message : null}
						fullWidth
						margin="dense"
						autoComplete="off"
					/>
				)}
			/>
		</Grid>
		{RECIPIENT_FIELDS.map(({ name, type }) => (
			<Grid item xs={12} md={4} key={name}>
				<TopicSelector
					control={control}
					name={name}
					type={type}
					topics={topics}
				/>
			</Grid>
		))}
	</Grid>
);
